export class BridgeUnavailableError extends Error {
  constructor() {
    super('Native bridge unavailable');
    this.name = 'BridgeUnavailableError';
  }
}

export interface NativeMessagePort {
  postMessage: (message: string) => void;
}

export interface BridgeEventTarget {
  addEventListener: (type: 'message', listener: (event: { data: unknown }) => void) => void;
  removeEventListener: (type: 'message', listener: (event: { data: unknown }) => void) => void;
}

export interface BridgeClientOptions {
  /** 30 000 ms, as `nativebridge.js`. */
  defaultTimeoutMs?: number;
  /** Resolves the native port on every call (the shell may inject it after page load). */
  getPort?: () => NativeMessagePort | undefined;
  eventTarget?: BridgeEventTarget;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

interface Pending {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  timeoutHandle: unknown;
}

interface BridgeResponse {
  action?: string;
  msgId?: string;
  payload?: unknown;
  error?: unknown;
  errorCode?: unknown;
}

declare global {
  interface Window {
    InviteNative?: NativeMessagePort;
    ReactNativeWebView?: NativeMessagePort;
  }
}

/**
 * Pick the first shell object that can actually receive messages. On Android `window.InviteNative`
 * is the raw `@JavascriptInterface` object (no `postMessage`) and only `window.ReactNativeWebView`
 * has one; iOS injects `postMessage` on both. So test each candidate instead of `??`-chaining them.
 */
export const resolveNativePort = (
  win: Pick<Window, 'InviteNative' | 'ReactNativeWebView'> | undefined
): NativeMessagePort | undefined => {
  if (win === undefined) {
    return undefined;
  }
  for (const candidate of [win.InviteNative, win.ReactNativeWebView]) {
    if (typeof candidate?.postMessage === 'function') {
      return candidate;
    }
  }
  return undefined;
};

const defaultGetPort = (): NativeMessagePort | undefined =>
  resolveNativePort(typeof window === 'undefined' ? undefined : window);

const guid = (): string => {
  const s4 = (): string =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

/**
 * `nativebridge.js` `window.webViewBridge.send`: posts `{ method, args, msgId }` to the native shell
 * and resolves from the `message` event carrying `{ action: 'response', msgId, payload | error }`.
 * Rejections: `BridgeUnavailableError`, the string `'Timeout'`, the shell's `error` string, or
 * `{ message, code }` when the shell sent an `errorCode`.
 */
export class BridgeClient {
  private readonly pending = new Map<string, Pending>();
  private readonly defaultTimeoutMs: number;
  private readonly getPort: () => NativeMessagePort | undefined;
  private readonly eventTarget: BridgeEventTarget | undefined;
  private readonly timers: {
    setTimeout: (fn: () => void, ms: number) => unknown;
    clearTimeout: (handle: unknown) => void;
  };
  private readonly onMessage = (event: { data: unknown }): void => {
    let message: BridgeResponse;
    try {
      message = (
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data
      ) as BridgeResponse;
    } catch {
      return;
    }
    if (message.action !== 'response' || typeof message.msgId !== 'string') {
      return;
    }
    const entry = this.pending.get(message.msgId);
    if (entry === undefined) {
      return;
    }
    this.pending.delete(message.msgId);
    this.timers.clearTimeout(entry.timeoutHandle);
    if (message.error !== undefined && message.error !== null) {
      entry.reject(
        message.errorCode !== undefined && message.errorCode !== null
          ? { message: message.error, code: message.errorCode }
          : message.error
      );
    } else {
      entry.resolve(message.payload);
    }
  };

  constructor(options: BridgeClientOptions = {}) {
    this.defaultTimeoutMs = options.defaultTimeoutMs ?? 30000;
    this.getPort = options.getPort ?? defaultGetPort;
    this.eventTarget = options.eventTarget ?? (typeof window === 'undefined' ? undefined : window);
    this.timers = {
      setTimeout: options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms)),
      clearTimeout:
        options.clearTimeout ??
        ((handle) => {
          clearTimeout(handle as ReturnType<typeof setTimeout>);
        })
    };
    this.eventTarget?.addEventListener('message', this.onMessage);
  }

  isAvailable(): boolean {
    return this.getPort() !== undefined;
  }

  send<T = unknown>(method: string, ...args: unknown[]): Promise<T> {
    return this.sendWithTimeout<T>(method, args, this.defaultTimeoutMs);
  }

  sendWithTimeout<T = unknown>(method: string, args: unknown[], timeoutMs: number): Promise<T> {
    const port = this.getPort();
    if (port === undefined) {
      return Promise.reject(new BridgeUnavailableError());
    }
    const msgId = guid();
    const message = JSON.stringify({ method, args, msgId });
    return new Promise<T>((resolve, reject) => {
      const timeoutHandle = this.timers.setTimeout(() => {
        this.pending.delete(msgId);
        // nativebridge.js rejects with the bare string; keybox.js maps it through errorCodes.Timeout.
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject('Timeout');
      }, timeoutMs);
      this.pending.set(msgId, {
        resolve: (value) => {
          resolve(value as T);
        },
        reject,
        timeoutHandle
      });
      port.postMessage(message);
    });
  }

  /** `window.invite.nativeAppPresence`: `deviceInfo` with a 3 s timeout; false on any failure. */
  async nativeAppPresence(): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }
    try {
      const info = await this.sendWithTimeout('deviceInfo', [null], 3000);
      return info !== null && info !== undefined && info !== false;
    } catch {
      return false;
    }
  }

  destroy(): void {
    this.eventTarget?.removeEventListener('message', this.onMessage);
    for (const entry of this.pending.values()) {
      this.timers.clearTimeout(entry.timeoutHandle);
    }
    this.pending.clear();
  }
}
